import { EventType, emitPageAction } from 'src/shared/telemetry';

import type { Context, Event, HistoryLoadedEvent } from './types';

/*
When the building is initially loaded, the building machine's updateStyles 
action is called 4 times to update the default styles for each aspect of
the building (currently once each for ROOFS, WALLS, WINDOWS, TRIM), this is
represented by variable REQUIRED_INITIAL_STYLE_UPDATES. As we continue to 
update design-studio with more features and default styles we'll need to 
keep this value updated with the number times updateStyles is called when 
loading a building in order to keep the loading analytics accurate.
*/
export const REQUIRED_INITIAL_STYLE_UPDATES = 4;

const getRequiredStyleUpdatesFromHistoryEvent = (event: Event) => {
  const { savedStylesToUpdate } = event as HistoryLoadedEvent;
  const potentialStyleUpdatesArray = Object.entries(savedStylesToUpdate);
  const numberOfSavedStyleUpdates = potentialStyleUpdatesArray.reduce(
    (accum, [, { color, textureId, gridType, frameType }]) =>
      color || textureId || gridType || frameType ? accum + 1 : accum,
    0,
  );
  return numberOfSavedStyleUpdates;
};

const shouldSendDefaultStylesEvent = ({ loadingStyleUpdates }: Context) =>
  loadingStyleUpdates + 1 === REQUIRED_INITIAL_STYLE_UPDATES;

const shouldSendSavedStylesEvent = ({
  savedStyleUpdates,
  loadingStyleUpdates,
  sentHistoryLoadedEvent,
}: Context) => {
  const savedStylesApplied =
    sentHistoryLoadedEvent &&
    loadingStyleUpdates + 1 >=
      REQUIRED_INITIAL_STYLE_UPDATES + savedStyleUpdates;
  return {
    markComplete: savedStylesApplied,
    sendEvent: savedStylesApplied && savedStyleUpdates > 0,
  };
};

const sendBuildingLoadTrackingEvents = (ctx: Context) => {
  const { sentSavedStylesAppliedEvent, loadingStyleUpdates } = ctx;
  const resultContext = {} as Context;
  if (!sentSavedStylesAppliedEvent) {
    if (shouldSendDefaultStylesEvent(ctx)) {
      emitPageAction(EventType.DEFAULT_STYLES_APPLIED, {
        designStudioMode: ctx.mode,
      });
    }
    const { sendEvent, markComplete } = shouldSendSavedStylesEvent(ctx);
    if (sendEvent) {
      emitPageAction(EventType.SAVED_USER_STYLES_APPLIED, {
        designStudioMode: ctx.mode,
      });
    }
    resultContext.loadingStyleUpdates = loadingStyleUpdates + 1;
    resultContext.sentSavedStylesAppliedEvent = markComplete;
  }
  return resultContext;
};

export {
  sendBuildingLoadTrackingEvents,
  getRequiredStyleUpdatesFromHistoryEvent,
};
